import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  Tag,
  Descriptions,
  Space,
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  Select,
  message,
  Spin,
  DatePicker,
} from "antd";
import { PlusOutlined, PrinterOutlined, DeleteOutlined, PayCircleOutlined } from "@ant-design/icons";
import { adminFolioService } from "../../../services/adminService";
import { PrintReceiptModal, PrintCustomerDetails } from "../../common/PrintReceiptModal";
import type { FrontDeskBooking } from "../../../types";
import { PAYMENT_METHOD_OPTIONS } from "../../../constants/paymentMethods";
import dayjs from "dayjs";
import { hasPermission } from "../../../utils/permissions";

const { RangePicker } = DatePicker;

interface FolioEntry {
  id: string;
  entry_type: string;
  category: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount: number;
  tax: number;
  total: number;
  entry_date: string;
  entry_time: string;
  notes?: string;
  created_at: string;
  staff_id?: string;
  staff_name?: string;
  method?: string;
  folio_id?: string;
  source_id?: string;
  reference_type?: string;
}

interface FolioSummary {
  total_charges: number;
  total_payments: number;
  balance: number;
  total_discount: number;
  total_tax: number;
  opening_balance?: number;
}

interface CategoryTotal {
  category: string;
  total: number;
}

interface FolioViewProps {
  bookingId: string;
  booking: FrontDeskBooking;
  groupBookingIds?: string[];
}

const CATEGORY_LABELS: Record<string, string> = {
  room: "Room",
  package: "Package",
  restaurant: "Restaurant",
  room_service: "Room Service",
  takeaway: "Takeaway",
  package_inclusion: "Inclusion",
  fee: "Fee",
  laundry: "Laundry",
  ironing: "Ironing",
  fine: "Fine",
  payment: "Payment",
  adjustment: "Adjustment",
  extra_service: "Extra Service",
  car_rental: "Car Rental",
  motorbike_rental: "Motorbike Rental",
  car_wash: "Car Wash",
  bicycle_rental: "Bicycle Rental",
  airport_transfer: "Airport Transfer",
  boat_trip: "Boat Trip",
  private_trip: "Private Trip",
  driver_service: "Driver Service",
  discount: "Discount",
  tax: "Tax",
  deposit: "Deposit",
};

const CATEGORY_COLORS: Record<string, string> = {
  room: "blue",
  package: "purple",
  restaurant: "green",
  room_service: "orange",
  takeaway: "volcano",
  package_inclusion: "cyan",
  fee: "gold",
  laundry: "magenta",
  ironing: "geekblue",
  fine: "red",
  payment: "volcano",
  adjustment: "default",
  extra_service: "geekblue",
  car_rental: "volcano",
  motorbike_rental: "orange",
  car_wash: "cyan",
  bicycle_rental: "green",
  airport_transfer: "purple",
  boat_trip: "blue",
  private_trip: "gold",
  driver_service: "magenta",
  discount: "volcano",
  tax: "blue",
  deposit: "green",
};

const fmt = (v: number) =>
  `LKR ${Number(v).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const canRemoveFolioEntries = (): boolean => hasPermission("billing:edit");
// Manual charges & payment recording are billing:edit on the backend.
const canRecordPayment = (): boolean => hasPermission("billing:edit");
const isRemovableEntry = (r: FolioEntry): boolean => {
  if (r.entry_type !== "charge") return false;
  if (r.reference_type === "pos_order" || r.reference_type === "laundry_order") return false;
  if ((r.category === "room" || r.category === "package") && !r.staff_id) return false;
  if (r.category === "deposit") return false;
  return true;
};

const FolioView: React.FC<FolioViewProps> = ({ bookingId, booking, groupBookingIds }) => {
  const [folioId, setFolioId] = useState<string | null>(null);
  const [folioStatus, setFolioStatus] = useState<string>("open");
  const [entries, setEntries] = useState<FolioEntry[]>([]);
  const [summary, setSummary] = useState<FolioSummary | null>(null);
  const [categoryTotals, setCategoryTotals] = useState<CategoryTotal[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [statementPreset, setStatementPreset] = useState<string | null>(null);
  const [chargeModalOpen, setChargeModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [manualChargeModalOpen, setManualChargeModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [chargeForm] = Form.useForm();
  const [paymentForm] = Form.useForm();
  const [manualChargeForm] = Form.useForm();
  const [manualChargeLines, setManualChargeLines] = useState<Array<{
    key: number;
    category: string;
    description: string;
    quantity: number;
    unit_price: number;
    discount: number;
    tax: number;
    entry_date: any;
    notes: string;
    showAdvanced: boolean;
  }>>([]);
  const [manualChargeSubmitting, setManualChargeSubmitting] = useState(false);
  const manualChargeNextKey = React.useRef(1);

  const fetchData = async () => {
    try {
      setLoading(true);
      const idsToFetch = groupBookingIds && groupBookingIds.length > 0 ? groupBookingIds : [bookingId];
      const dateParams = dateRange
        ? { from_date: dateRange[0].format("YYYY-MM-DD"), to_date: dateRange[1].format("YYYY-MM-DD") }
        : undefined;

      const folioResults = await Promise.all(
        idsToFetch.map(async (id) => {
          try {
            const res = await adminFolioService.getByBooking(id);
            return (res as any).data || null;
          } catch {
            return null;
          }
        })
      );

      const validFolios = folioResults.filter(Boolean);
      if (validFolios.length === 0) return;

      setFolioId(validFolios[0].id);
      setFolioStatus(validFolios[0].status || "open");

      const allFolioIds = validFolios.map((f: any) => f.id);

      const entriesResults = await Promise.all(
        allFolioIds.map((fid: string) => adminFolioService.getEntries(fid, dateParams).catch(() => ({ data: [] })))
      );
      const summaryResults = await Promise.all(
        allFolioIds.map((fid: string) => adminFolioService.getSummary(fid, dateParams).catch(() => ({ data: null })))
      );
      const catResults = await Promise.all(
        allFolioIds.map((fid: string) => adminFolioService.getCategoryTotals(fid, dateParams).catch(() => ({ data: [] })))
      );

      const mergedEntries: FolioEntry[] = [];
      entriesResults.forEach((e: any) => {
        const rows = Array.isArray(e?.data) ? e.data : [];
        rows.forEach((entry: FolioEntry, idx: number) => {
          mergedEntries.push({ ...entry, source_id: entry.id, id: `${entry.id}-${idx}` });
        });
      });
      setEntries(mergedEntries);

      const mergedSummary: FolioSummary = { total_charges: 0, total_payments: 0, balance: 0, total_discount: 0, total_tax: 0, opening_balance: 0 };
      summaryResults.forEach((s: any) => {
        const d = s?.data;
        if (d) {
          mergedSummary.total_charges += Number(d.total_charges || 0);
          mergedSummary.total_payments += Number(d.total_payments || 0);
          mergedSummary.total_discount += Number(d.total_discount || 0);
          mergedSummary.total_tax += Number(d.total_tax || 0);
          mergedSummary.balance += Number(d.balance || 0);
          mergedSummary.opening_balance = (mergedSummary.opening_balance || 0) + Number(d.opening_balance || 0);
        }
      });
      setSummary(mergedSummary);

      const catMap = new Map<string, number>();
      catResults.forEach((c: any) => {
        const rows = Array.isArray(c?.data) ? c.data : [];
        rows.forEach((cat: CategoryTotal) => {
          catMap.set(cat.category, (catMap.get(cat.category) || 0) + Number(cat.total || 0));
        });
      });
      setCategoryTotals(Array.from(catMap.entries()).map(([category, total]) => ({ category, total })));
    } catch {
      message.error("Failed to load folio");
    } finally {
      setLoading(false);
    }
  };

  // The folio mirrors this booking's charges, so editing a fee or a discount
  // has to re-fetch it. `bookingId` alone never changes here — that is exactly
  // why an edit stayed invisible until a full page reload. Serialised rather
  // than keyed on `updated_at`: in a group the fee is often written against a
  // sibling row, while the record the folio shows is the holder's, and
  // `groupBookingIds` would otherwise be a fresh array on every render.
  const bookingSignature = useMemo(
    () => JSON.stringify({ id: bookingId, booking, groupBookingIds }),
    [bookingId, booking, groupBookingIds],
  );

  useEffect(() => { fetchData(); }, [bookingSignature, dateRange]);

  const handlePostCharge = async (values: any) => {
    if (!folioId) return;
    try {
      await adminFolioService.postCharge(folioId, {
        ...values,
        entry_date: values.entry_date?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
      });
      message.success("Charge posted");
      setChargeModalOpen(false);
      chargeForm.resetFields();
      fetchData();
    } catch (err: any) {
      message.error(err?.message || "Failed");
    }
  };

  const handleRecordPayment = async (values: any) => {
    if (!folioId) return;
    try {
      await adminFolioService.postPayment(folioId, {
        ...values,
        entry_date: values.entry_date?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
      });
      message.success("Payment recorded");
      setPaymentModalOpen(false);
      paymentForm.resetFields();
      fetchData();
    } catch (err: any) {
      message.error(err?.message || "Failed");
    }
  };

  const handleRemoveEntry = (row: FolioEntry) => {
    if (!row.folio_id || !row.source_id) return;
    Modal.confirm({
      title: "Remove charge?",
      content: `"${row.description}" (${fmt(Number(row.total || 0))}) will be removed from this folio.`,
      okText: "Remove",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          await adminFolioService.deleteEntry(row.folio_id!, row.source_id!);
          message.success("Charge removed");
          fetchData();
        } catch (err: any) {
          message.error(err?.message || "Failed to remove charge");
        }
      },
    });
  };

  const addManualChargeLine = () => {
    const key = manualChargeNextKey.current++;
    setManualChargeLines((prev) => [
      ...prev,
      {
        key,
        category: "extra_service",
        description: "",
        quantity: 1,
        unit_price: 0,
        discount: 0,
        tax: 0,
        entry_date: dayjs(),
        notes: "",
        showAdvanced: false,
      },
    ]);
  };

  const removeManualChargeLine = (key: number) => {
    setManualChargeLines((prev) => prev.filter((l) => l.key !== key));
  };

  const updateManualChargeLine = (key: number, field: string, value: any) => {
    setManualChargeLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, [field]: value } : l))
    );
  };

  const getManualChargeTotals = () => {
    let subtotal = 0;
    let totalDiscount = 0;
    let totalTax = 0;
    for (const line of manualChargeLines) {
      subtotal += line.quantity * line.unit_price;
      totalDiscount += line.discount;
      totalTax += line.tax;
    }
    return { subtotal, totalDiscount, totalTax, grandTotal: subtotal - totalDiscount + totalTax };
  };

  const handleSubmitManualCharges = async () => {
    if (!folioId || manualChargeLines.length === 0) return;

    const hasEmpty = manualChargeLines.some((l) => !l.description.trim());
    if (hasEmpty) {
      message.warning("Please fill in the description for all lines");
      return;
    }

    setManualChargeSubmitting(true);
    try {
      const payload = manualChargeLines.map((l) => ({
        category: l.category,
        description: l.description.trim(),
        quantity: l.quantity,
        unit_price: l.unit_price,
        discount: l.discount,
        tax: l.tax,
        entry_date: l.entry_date?.format("YYYY-MM-DD") || dayjs().format("YYYY-MM-DD"),
        notes: l.notes || undefined,
      }));
      await adminFolioService.postBatchCharges(folioId, payload);
      message.success(`${payload.length} charge(s) posted`);
      setManualChargeModalOpen(false);
      setManualChargeLines([]);
      manualChargeNextKey.current = 1;
      fetchData();
    } catch (err: any) {
      message.error(err?.message || "Failed to post charges");
    } finally {
      setManualChargeSubmitting(false);
    }
  };

  const handlePrintFolio = async (customer?: PrintCustomerDetails): Promise<boolean> => {
    if (!folioId) return false;
    try {
      const params: { groupBookingIds?: string[]; from_date?: string; to_date?: string } = {};
      if (groupBookingIds && groupBookingIds.length > 0) params.groupBookingIds = groupBookingIds;
      if (dateRange) {
        params.from_date = dateRange[0].format("YYYY-MM-DD");
        params.to_date = dateRange[1].format("YYYY-MM-DD");
      }
      const blob = await adminFolioService.getReceipt(
        folioId,
        Object.keys(params).length > 0 ? params : undefined,
        customer,
      );
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
      return true;
    } catch {
      message.error("Failed to generate folio receipt");
      return false;
    }
  };

  // Build table data with running balance
  let processedEntries = entries;

  if (groupBookingIds && groupBookingIds.length > 0) {
    const aggMap = new Map<string, FolioEntry>();
    for (const entry of entries) {
      const key = `${entry.entry_date}|${entry.category}|${entry.description}|${entry.unit_price}`;
      if (aggMap.has(key)) {
          const existing = aggMap.get(key)!;
          existing.quantity = Number(existing.quantity) + (Number(entry.quantity) || 0);
          existing.total = Number(existing.total) + (Number(entry.total) || 0);
      } else {
        aggMap.set(key, { ...entry });
      }
    }
    processedEntries = Array.from(aggMap.values());
  }

  const applyStatementPreset = (key: string) => {
    const today = dayjs();
    if (key === "week") {
      setDateRange([today.subtract((today.day() + 6) % 7, "day"), today]);
    } else if (key === "last15") {
      setDateRange([today.subtract(14, "day"), today]);
    } else if (key === "month") {
      setDateRange([today.startOf("month"), today]);
    } else if (key === "balance") {
      setDateRange(null);
    }
    setStatementPreset(key);
  };

  let balance = Number(summary?.opening_balance || 0);
  const sortedRows = processedEntries
    .sort((a, b) => a.entry_date.localeCompare(b.entry_date) || a.entry_time?.localeCompare(b.entry_time))
    .map((entry) => {
      balance += Number(entry.total) || 0;
      return { ...entry, key: entry.id, rowType: "entry" as const, running_balance: balance };
    });

  const tableData: any[] = [];
  let prevDate: string | null = null;
  let dayTotal = 0;
  let dayEntries = 0;
  let lastRunning = Number(summary?.opening_balance || 0);
  for (const row of sortedRows) {
    if (prevDate && row.entry_date !== prevDate) {
      tableData.push({
        key: `s-${prevDate}`,
        rowType: "subtotal",
        entry_date: prevDate,
        dayTotal,
        dayEntries,
        running_balance: lastRunning,
      });
      dayTotal = 0;
      dayEntries = 0;
    }
    if (row.entry_date !== prevDate) {
      tableData.push({ key: `g-${row.entry_date}`, rowType: "group", entry_date: row.entry_date });
    }
    prevDate = row.entry_date;
    dayTotal += Number(row.total) || 0;
    dayEntries += 1;
    lastRunning = row.running_balance;
    tableData.push(row);
  }
  if (prevDate) {
    tableData.push({
      key: `s-${prevDate}`,
      rowType: "subtotal",
      entry_date: prevDate,
      dayTotal,
      dayEntries,
      running_balance: lastRunning,
    });
  }

  if (loading) return <Spin style={{ display: "block", margin: "40px auto" }} />;
  if (!folioId) return <div style={{ color: "#999", textAlign: "center", padding: 24 }}>No folio found.</div>;

  const nights = Math.max(1, dayjs(booking.check_out_date).diff(dayjs(booking.check_in_date), "day"));

  const columns: any[] = [
    {
      title: "Date",
      dataIndex: "entry_date",
      key: "date",
      width: 90,
      render: (d: string, r: any) => (r.rowType === "entry" ? dayjs(d).format("DD/MM/YY") : ""),
    },
    {
      title: "Time",
      key: "time",
      width: 65,
      render: (_: any, r: any) => {
        if (r.rowType !== "entry" || !r.entry_time) return "";
        const t = dayjs(r.entry_time);
        return t.isValid() ? t.format("HH:mm") : "";
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (text: string, r: any) => {
        if (r.rowType === "group") {
          return (
            <strong style={{ fontSize: 13, color: "#1677ff" }}>
              {dayjs(r.entry_date).format("ddd, DD MMM YYYY")}
            </strong>
          );
        }
        if (r.rowType === "subtotal") {
          return (
            <span style={{ fontStyle: "italic", color: "#555", float: "right" }}>
              Day subtotal ({r.dayEntries} {r.dayEntries === 1 ? "entry" : "entries"})
            </span>
          );
        }
        return (
          <Space size={4}>
            <Tag color={CATEGORY_COLORS[r.category] || "default"} style={{ margin: 0 }}>
              {CATEGORY_LABELS[r.category] || r.category}
            </Tag>
            {text}
          </Space>
        );
      },
    },
    {
      title: "Staff",
      key: "staff",
      width: 110,
      ellipsis: true,
      render: (_: any, r: any) =>
        r.rowType === "entry" ? (r.staff_name || <span style={{ color: "#bbb" }}>—</span>) : "",
    },
    {
      title: "Qty",
      dataIndex: "quantity",
      key: "qty",
      width: 55,
      align: "right" as const,
      render: (v: number, r: any) => (r.rowType === "entry" ? v : ""),
    },
    {
      title: "Unit Price",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 95,
      align: "right" as const,
      render: (v: number, r: any) => (r.rowType === "entry" ? fmt(v) : ""),
    },
    {
      title: "Amount",
      dataIndex: "total",
      key: "amount",
      width: 120,
      align: "right" as const,
      render: (v: number, r: any) => {
        if (r.rowType === "group") return "";
        if (r.rowType === "subtotal") {
          return (
            <strong style={{ color: "#555" }}>{fmt(Number(r.dayTotal || 0))}</strong>
          );
        }
        const isNegative = r.entry_type === "payment" || v < 0;
        return (
          <span style={{ fontWeight: 600, color: isNegative ? "#ff4d4f" : undefined }}>
            {isNegative ? "-" : ""}{fmt(Math.abs(v))}
          </span>
        );
      },
    },
    {
      title: "Balance",
      dataIndex: "running_balance",
      key: "balance",
      width: 120,
      align: "right" as const,
      render: (v: number, r: any) => {
        if (r.rowType === "group") return "";
        if (r.rowType === "subtotal") {
          return <strong style={{ color: "#555" }}>{fmt(v)}</strong>;
        }
        return <span style={{ fontWeight: 500 }}>{fmt(v)}</span>;
      },
    },
    {
      title: "",
      key: "actions",
      width: 50,
      align: "center" as const,
      render: (_: any, r: any) => {
        if (r.rowType !== "entry") return null;
        if (!canRemoveFolioEntries() || folioStatus !== "open") return null;
        if (groupBookingIds && groupBookingIds.length > 0) return null;
        if (!r.source_id || !r.folio_id || !isRemovableEntry(r)) return null;
        return (
          <Button
            danger
            size="small"
            type="text"
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveEntry(r)}
          />
        );
      },
    },
  ];

  return (
    <div>
      {/* Header */}
      <Descriptions bordered size="small" column={4} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Guest">{booking.guest_name}</Descriptions.Item>
        <Descriptions.Item label="Room">{booking.room_number || "N/A"}</Descriptions.Item>
        <Descriptions.Item label="Check-in">{dayjs(booking.check_in_date).format("DD/MM/YYYY")}</Descriptions.Item>
        <Descriptions.Item label="Check-out">{dayjs(booking.check_out_date).format("DD/MM/YYYY")}</Descriptions.Item>
        <Descriptions.Item label="Nights">{nights}</Descriptions.Item>
        <Descriptions.Item label="Package">{booking.package_name || "None"}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={folioStatus === "open" ? "green" : "default"}>
            {folioStatus === "open" ? "Open" : "Closed"}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Balance">
          {(() => {
            const headerBalance = (summary?.opening_balance || 0) + (summary?.balance || 0);
            return (
              <span style={{ fontWeight: 700, fontSize: 16, color: headerBalance > 0 ? "#fa8c16" : "#52c41a" }}>
                {fmt(headerBalance)}
              </span>
            );
          })()}
        </Descriptions.Item>
      </Descriptions>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            value={dateRange}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setDateRange([dates[0], dates[1]]);
              } else {
                setDateRange(null);
              }
              setStatementPreset(null);
            }}
            format="YYYY-MM-DD"
            allowClear
            placeholder={["Period from", "Period to"]}
          />
          <Space.Compact>
            <Button
              size="small"
              type={statementPreset === "week" ? "primary" : "default"}
              onClick={() => applyStatementPreset("week")}
            >
              This Week
            </Button>
            <Button
              size="small"
              type={statementPreset === "last15" ? "primary" : "default"}
              onClick={() => applyStatementPreset("last15")}
            >
              Last 15 Days
            </Button>
            <Button
              size="small"
              type={statementPreset === "month" ? "primary" : "default"}
              onClick={() => applyStatementPreset("month")}
            >
              This Month
            </Button>
            <Button
              size="small"
              type={statementPreset === "balance" ? "primary" : "default"}
              onClick={() => applyStatementPreset("balance")}
            >
              Current Balance
            </Button>
          </Space.Compact>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setManualChargeLines([]);
              manualChargeNextKey.current = 1;
              addManualChargeLine();
              setManualChargeModalOpen(true);
            }}
            disabled={folioStatus !== "open" || !canRecordPayment}
          >
            Add Manual Charge
          </Button>
          <Button
            icon={<PayCircleOutlined />}
            onClick={() => {
              const outstanding =
                summary && Number(summary.balance) > 0
                  ? Math.round(Number(summary.balance) * 100) / 100
                  : undefined;
              paymentForm.setFieldsValue({
                description: "Payment received",
                amount: outstanding,
                method: "cash",
                entry_date: dayjs(),
              });
              setPaymentModalOpen(true);
            }}
            disabled={folioStatus !== "open" || !canRecordPayment}
          >
            Record Payment
          </Button>
          <Button icon={<PrinterOutlined />} onClick={() => setPrintModalOpen(true)}>
            Print Folio
          </Button>
        </Space>
      </div>

      {/* Active period chip */}
      {dateRange && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginBottom: 12,
            padding: "6px 12px",
            background: "#f0f7ff",
            border: "1px solid #d6e4ff",
            borderRadius: 6,
            fontSize: 13,
          }}
        >
          <span>
            <strong>Statement Period:</strong> {dateRange[0].format("DD MMM YYYY")} – {dateRange[1].format("DD MMM YYYY")}
          </span>
          <a onClick={() => setDateRange(null)} style={{ fontSize: 13 }}>Clear</a>
        </div>
      )}

      {/* Entries Table */}
      <Table
        rowKey="key"
        columns={columns}
        dataSource={tableData}
        pagination={false}
        size="small"
      />

      {/* Totals */}
      {tableData.length > 0 && (() => {
        const charges = categoryTotals.filter(c => !['payment', 'adjustment'].includes(c.category));

        return (
          <div style={{ marginTop: 12, padding: "12px 16px", background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
            {/* Balance Brought Forward (statement period) */}
            {dateRange && (
              <>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>
                  Opening Balance
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, borderBottom: "1px solid #e8e8e8", paddingBottom: 6 }}>
                  <strong>Balance Brought Forward</strong>
                  <strong>{fmt(summary?.opening_balance || 0)}</strong>
                </div>
              </>
            )}

            {/* Category Breakdown */}
            {charges.length > 0 && (
              <>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>
                  Category Breakdown
                </div>
                {charges.map(c => (
                  <div key={c.category} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                    <span>{CATEGORY_LABELS[c.category] || c.category}</span>
                    <span>{fmt(Number(c.total))}</span>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, borderTop: "1px solid #e8e8e8", paddingTop: 6 }}>
                  <strong>Subtotal</strong>
                  <strong>{fmt(summary?.total_charges || 0)}</strong>
                </div>
              </>
            )}

            {/* Discount / Tax totals */}
            {((summary?.total_discount || 0) > 0 || (summary?.total_tax || 0) > 0) && (
              <div style={{ marginBottom: 12 }}>
                {(summary?.total_discount || 0) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#fa8c16" }}>
                    <span>Total Discounts</span>
                    <strong>-{fmt(summary?.total_discount || 0)}</strong>
                  </div>
                )}
                {(summary?.total_tax || 0) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#1677ff" }}>
                    <span>Total Tax</span>
                    <strong>{fmt(summary?.total_tax || 0)}</strong>
                  </div>
                )}
              </div>
            )}

            {/* Payments */}
            {(summary?.total_payments || 0) > 0 && (
              <>
                <div style={{ fontSize: 12, color: "#888", fontWeight: 600, marginBottom: 6, textTransform: "uppercase" }}>
                  Payments Received
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, color: "#ff4d4f" }}>
                  <span>Total Paid</span>
                  <strong>-{fmt(summary?.total_payments || 0)}</strong>
                </div>
                <div style={{ borderTop: "1px solid #e8e8e8", paddingTop: 6, marginTop: 4 }} />
              </>
            )}

            {/* Balance Due / Balance Carried Forward */}
            {(() => {
              const closing = (summary?.opening_balance || 0) + (summary?.balance || 0);
              return (
                <div style={{ display: "flex", justifyContent: "space-between", paddingTop: 4 }}>
                  <strong>{dateRange ? "Balance Carried Forward" : "Balance Due"}</strong>
                  <strong style={{ fontSize: 16, color: closing > 0 ? "#fa8c16" : "#52c41a" }}>
                    {fmt(closing)}
                  </strong>
                </div>
              );
            })()}
          </div>
        );
      })()}

      {/* Post Charge Modal */}
      <Modal title="Post Charge" open={chargeModalOpen} onCancel={() => setChargeModalOpen(false)} footer={null} destroyOnClose>
        <Form form={chargeForm} layout="vertical" onFinish={handlePostCharge}>
          <Form.Item name="category" label="Category" rules={[{ required: true }]}>
            <Select options={Object.entries(CATEGORY_LABELS).filter(([k]) => k !== "payment").map(([k, v]) => ({ label: v, value: k }))} />
          </Form.Item>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input placeholder="e.g. Ironing, Car Wash" />
          </Form.Item>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Form.Item name="quantity" label="Qty" initialValue={1} rules={[{ required: true }]}>
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="unit_price" label="Unit Price (LKR)" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <Form.Item name="entry_date" label="Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setChargeModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">Post</Button>
          </div>
        </Form>
      </Modal>

      {/* Manual Charge Modal */}
      <Modal
        title="Add Manual Charge"
        open={manualChargeModalOpen}
        onCancel={() => setManualChargeModalOpen(false)}
        width={680}
        footer={
          manualChargeLines.length > 0
            ? [
                <Button key="cancel" onClick={() => setManualChargeModalOpen(false)}>
                  Cancel
                </Button>,
                <Button
                  key="submit"
                  type="primary"
                  style={{ backgroundColor: "#B79A68", borderColor: "#B79A68" }}
                  loading={manualChargeSubmitting}
                  onClick={handleSubmitManualCharges}
                >
                  Post {manualChargeLines.length} Charge{manualChargeLines.length > 1 ? "s" : ""} — {fmt(getManualChargeTotals().grandTotal)}
                </Button>,
              ]
            : null
        }
        destroyOnClose
      >
        {/* Charge Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {manualChargeLines.map((line) => {
            const lineTotal = line.quantity * line.unit_price - line.discount + line.tax;
            return (
              <div
                key={line.key}
                style={{
                  backgroundColor: "#fff",
                  padding: 16,
                  borderRadius: 8,
                  border: "1px solid #e8e8e8",
                }}
              >
                {/* Row 1: Description + Remove */}
                <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                  <Input
                    placeholder="Service description (e.g. Car Wash, Airport Transfer)"
                    value={line.description}
                    onChange={(e) => updateManualChargeLine(line.key, "description", e.target.value)}
                    style={{ flex: 1, fontWeight: 500 }}
                    autoFocus
                  />
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeManualChargeLine(line.key)}
                    style={{ flexShrink: 0 }}
                  />
                </div>

                {/* Row 2: Category, Qty, Unit Price, Date */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 1fr 130px", gap: 8, marginBottom: 8 }}>
                  <Select
                    value={line.category}
                    onChange={(val) => updateManualChargeLine(line.key, "category", val)}
                    options={Object.entries(CATEGORY_LABELS)
                      .filter(([k]) => !["payment", "adjustment", "discount", "tax", "package_inclusion"].includes(k))
                      .map(([k, v]) => ({ label: v, value: k }))}
                    size="middle"
                  />
                  <InputNumber
                    min={1}
                    value={line.quantity}
                    onChange={(val) => updateManualChargeLine(line.key, "quantity", val || 1)}
                    style={{ width: "100%" }}
                    size="middle"
                  />
                  <InputNumber
                    min={0}
                    value={line.unit_price}
                    onChange={(val) => updateManualChargeLine(line.key, "unit_price", val || 0)}
                    style={{ width: "100%" }}
                    size="middle"
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    placeholder="Unit Price"
                  />
                  <DatePicker
                    value={line.entry_date}
                    onChange={(val) => updateManualChargeLine(line.key, "entry_date", val)}
                    format="DD/MM/YY"
                    size="middle"
                  />
                </div>

                {/* Row 2 labels */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 80px 1fr 130px", gap: 8, marginTop: -4, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, color: "#888" }}>Category</span>
                  <span style={{ fontSize: 11, color: "#888" }}>Qty</span>
                  <span style={{ fontSize: 11, color: "#888" }}>Unit Price (LKR)</span>
                  <span style={{ fontSize: 11, color: "#888" }}>Date</span>
                </div>

                {/* Row 3: Line Total */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <Button
                    type="link"
                    size="small"
                    style={{ padding: 0, fontSize: 12, color: "#888" }}
                    onClick={() => updateManualChargeLine(line.key, "showAdvanced", !line.showAdvanced)}
                  >
                    {line.showAdvanced ? "▾ Hide" : "▸ Show"} Discount, Tax & Notes
                  </Button>
                  <span style={{ fontWeight: 600, color: "#B79A68", fontSize: 14 }}>
                    LKR {lineTotal.toLocaleString()}
                  </span>
                </div>

                {/* Advanced: Discount, Tax, Notes (collapsible) */}
                {line.showAdvanced && (
                  <div style={{ marginTop: 8, padding: 12, background: "#fafafa", borderRadius: 6, border: "1px solid #f0f0f0" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Discount (LKR)</div>
                        <InputNumber
                          min={0}
                          value={line.discount}
                          onChange={(val) => updateManualChargeLine(line.key, "discount", val || 0)}
                          style={{ width: "100%" }}
                          size="small"
                        />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Tax (LKR)</div>
                        <InputNumber
                          min={0}
                          value={line.tax}
                          onChange={(val) => updateManualChargeLine(line.key, "tax", val || 0)}
                          style={{ width: "100%" }}
                          size="small"
                        />
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#888", marginBottom: 2 }}>Notes</div>
                      <Input
                        value={line.notes}
                        onChange={(e) => updateManualChargeLine(line.key, "notes", e.target.value)}
                        placeholder="Optional notes..."
                        size="small"
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add Another Button */}
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={() => addManualChargeLine()}
          style={{ marginTop: 12 }}
        >
          Add Another Charge
        </Button>

        {/* Totals */}
        {manualChargeLines.length > 0 && (() => {
          const totals = getManualChargeTotals();
          return (
            <div
              style={{
                marginTop: 16,
                padding: "12px 16px",
                background: "#fafafa",
                borderRadius: 6,
                border: "1px solid #f0f0f0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, color: "#888" }}>
                {manualChargeLines.length} charge{manualChargeLines.length > 1 ? "s" : ""}
              </span>
              <Space size={24}>
                <span style={{ fontSize: 12, color: "#888" }}>
                  Subtotal: <strong>{fmt(totals.subtotal)}</strong>
                </span>
                {totals.totalDiscount > 0 && (
                  <span style={{ fontSize: 12, color: "#888" }}>
                    Discount: <strong style={{ color: "#ff4d4f" }}>-{fmt(totals.totalDiscount)}</strong>
                  </span>
                )}
                {totals.totalTax > 0 && (
                  <span style={{ fontSize: 12, color: "#888" }}>
                    Tax: <strong style={{ color: "#1890ff" }}>+{fmt(totals.totalTax)}</strong>
                  </span>
                )}
                <span style={{ fontSize: 16, fontWeight: 700, color: "#B79A68" }}>
                  {fmt(totals.grandTotal)}
                </span>
              </Space>
            </div>
          );
        })()}
      </Modal>

      {/* Record Payment Modal */}
      <Modal title="Record Payment" open={paymentModalOpen} onCancel={() => setPaymentModalOpen(false)} footer={null} destroyOnClose>
        <Form form={paymentForm} layout="vertical" onFinish={handleRecordPayment}>
          <Form.Item name="description" label="Description" rules={[{ required: true }]}>
            <Input placeholder="e.g. Cash, Card" />
          </Form.Item>
          <Form.Item name="amount" label="Amount (LKR)" rules={[{ required: true }]}>
            <InputNumber min={0.01} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="method" label="Payment Method">
            <Select options={PAYMENT_METHOD_OPTIONS} />
          </Form.Item>
          <Form.Item name="entry_date" label="Date">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setPaymentModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit">Record</Button>
          </div>
        </Form>
      </Modal>

      {/* Print receipt / tax invoice details */}
      <PrintReceiptModal
        open={printModalOpen}
        title="Print Folio"
        defaults={{
          customerName: booking.guest_name,
          phone: booking.phone || "",
          email: booking.email || "",
          address: [booking.address_line_1, booking.address_line_2].filter(Boolean).join(", "),
        }}
        onCancel={() => setPrintModalOpen(false)}
        onPrint={async (customer) => {
          const ok = await handlePrintFolio(customer);
          if (ok) setPrintModalOpen(false);
        }}
      />

    </div>
  );
};

export default FolioView;
